import ConfirmationDialog from '@rio-cloud/rio-uikit/ConfirmationDialog';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import Select, { SelectOption } from '@rio-cloud/rio-uikit/Select';
import { useState } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { usePutCompoundAccessMutation } from '../api/serviceProviderApi';
import { handleQueryError } from '../notifications/ErrorNotification';
import { showSuccessNotification } from '../notifications/SuccessNotification';
import { CompoundAccess, ServiceProvider } from './serviceProvider.types';

const compoundAccessRank: Record<CompoundAccess, number> = {
    [CompoundAccess.NONE]: 0,
    [CompoundAccess.READ_ONLY]: 1,
    [CompoundAccess.READ_WRITE]: 2,
};

const getCompoundAccessOptions = (intl: IntlShape): SelectOption[] => [
    {
        id: CompoundAccess.NONE,
        label: intl.formatMessage({ id: 'outboundOrderBook.serviceProviders.compoundAccess.none' }),
    },
    {
        id: CompoundAccess.READ_ONLY,
        label: intl.formatMessage({ id: 'outboundOrderBook.serviceProviders.compoundAccess.readOnly' }),
    },
    {
        id: CompoundAccess.READ_WRITE,
        label: intl.formatMessage({ id: 'outboundOrderBook.serviceProviders.compoundAccess.readWrite' }),
    },
];

export const CompoundAccessSelect = ({ serviceProvider }: { serviceProvider: ServiceProvider }) => {
    const intl = useIntl();
    const [selectedAccess, setSelectedAccess] = useState(serviceProvider.compoundAccess);
    const [requestedAccessDowngrade, setRequestedAccessDowngrade] = useState<CompoundAccess>();
    const [putCompoundAccess, compoundAccessRequest] = usePutCompoundAccessMutation();
    const inputId = `compound-access-${serviceProvider.accountId}`;
    // Necessary to keep the previous selection when the request dialog was aborted.
    // By changing the key we enforce a rerender to reset the selects internal state
    const selectKey = `${selectedAccess}-${requestedAccessDowngrade ?? 'confirmed'}`;

    const options = getCompoundAccessOptions(intl);

    const updateCompoundAccess = async (compoundAccess: CompoundAccess) => {
        setSelectedAccess(compoundAccess);
        try {
            await putCompoundAccess({ accountId: serviceProvider.accountId, compoundAccess }).unwrap();
            showSuccessNotification('outboundOrderBook.serviceProviders.compoundAccess.success');
        } catch (error: unknown) {
            setSelectedAccess(serviceProvider.compoundAccess);
            handleQueryError(error);
        }
    };

    const changeCompoundAccess = (option: SelectOption | undefined) => {
        const nextAccess = option?.id as CompoundAccess | undefined;
        if (nextAccess === undefined || nextAccess === selectedAccess) {
            return;
        }
        if (compoundAccessRank[nextAccess] < compoundAccessRank[selectedAccess]) {
            setRequestedAccessDowngrade(nextAccess);
        } else {
            void updateCompoundAccess(nextAccess);
        }
    };

    const confirmDowngrade = () => {
        if (requestedAccessDowngrade !== undefined) {
            void updateCompoundAccess(requestedAccessDowngrade);
        }
        setRequestedAccessDowngrade(undefined);
    };

    return (
        <>
            <Select
                key={selectKey}
                id={inputId}
                disabled={compoundAccessRequest.isLoading}
                options={options}
                value={[selectedAccess]}
                onChange={changeCompoundAccess}
            />
            <ConfirmationDialog
                show={requestedAccessDowngrade !== undefined}
                title={<FormattedMessage id="outboundOrderBook.serviceProviders.compoundAccess.confirmation.title" />}
                content={<FormattedMessage id="outboundOrderBook.serviceProviders.compoundAccess.confirmation.text" />}
                bsSize={Dialog.SIZE_SM}
                onClickConfirm={confirmDowngrade}
                onClickCancel={() => setRequestedAccessDowngrade(undefined)}
                cancelButtonText={<FormattedMessage id="outboundOrderBook.common.button.cancel" />}
                confirmButtonText={<FormattedMessage id="outboundOrderBook.common.button.apply" />}
            />
        </>
    );
};
